/**
 * Retrieves all keyboard focusable elements within a given container element.
 *
 * Features:
 * - Finds all elements that can receive keyboard focus
 * - Filters out disabled, hidden, and inert elements
 * - Respects ARIA attributes for accessibility
 * - Checks computed styles for visibility
 * - Supports all standard focusable elements (links, buttons, inputs, etc.)
 * - Handles null and undefined input gracefully
 * - Returns array of focusable HTMLElements
 * - Excludes elements with tabindex="-1"
 *
 * Focusable Elements Detected:
 * - Links with href attributes
 * - Buttons (all types)
 * - Form inputs (text, checkbox, radio, etc.)
 * - Textareas and select elements
 * - Details elements
 * - Elements with positive tabindex values
 *
 * @param element - The container element to search for focusable elements
 * @returns Array of focusable HTMLElements, or empty array if no element provided
 */
export function getKeyboardFocusableElements(element?: HTMLElement | null) {
  if (!element) return [];
  const elements = element.querySelectorAll(
    'a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])',
  );

  return Array.from(elements).filter(
    (el) =>
      !el.hasAttribute("disabled") &&
      !el.getAttribute("aria-hidden") &&
      !el.getAttribute("inert") &&
      !(globalThis.getComputedStyle(el).display === "none"),
  );
}
